import Link from 'next/link';
import { gql, useQuery } from '@apollo/client';
import { ButtonLink } from '../ui';
import { useAuth } from '../auth';
import { Plans, planDisplayNames } from './forms/account-plan';
import { trackUpgradeCtaClicked, plansUrlWithUpgradeSource } from '../analytics';

const NAV_PLAN_QUERY = gql`
  query NavPlan {
    getAccount {
      plan
    }
  }
`;

type NavPlanUpgradeProps = {
  source: 'template_sidebar' | 'dashboard_sidebar';
  plan?: string;
  loading?: boolean;
  className?: string;
};

const UpgradeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" aria-hidden="true">
    <g clipPath="url(#nav-plan-upgrade-icon)">
      <path
        fill="#fff"
        d="M13.63 6.18a.333.333 0 0 0-.297-.18H8.416L9.66.404a.333.333 0 0 0-.597-.267L2.395 9.471a.333.333 0 0 0 .272.529h4.917l-1.243 5.593a.333.333 0 0 0 .597.267l6.667-9.333a.333.333 0 0 0 .024-.347Z"
      />
    </g>
    <defs>
      <clipPath id="nav-plan-upgrade-icon">
        <path fill="#fff" d="M0 0h16v16H0z" />
      </clipPath>
    </defs>
  </svg>
);

export function NavPlanUpgrade({
  source,
  plan: planProp,
  loading: loadingProp,
  className = '',
}: NavPlanUpgradeProps) {
  const { user } = useAuth();
  const shouldFetch = !planProp && !!user;
  const { loading: queryLoading, data } = useQuery(NAV_PLAN_QUERY, {
    ssr: false,
    skip: !shouldFetch,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  if (!user) {
    return null;
  }

  const loading = loadingProp ?? (shouldFetch && queryLoading);
  if (loading) {
    return null;
  }

  const plan = planProp || data?.getAccount?.plan || 'FREE';
  const planKey = plan?.toUpperCase?.() || 'FREE';
  const planInfo = Plans[planKey] || Plans.FREE;
  const isFreePlan = planKey === 'FREE';
  const upgradeButtonId =
    source === 'template_sidebar' ? 'btn-upgrade-sidebar' : 'btn-upgrade-nav';

  return (
    <div
      className={`flex flex-col items-start gap-2 text-[11px] text-neutral-600 dark:text-white/70 ${className}`}
    >
      <p className="m-0 p-0">
        <strong>{`${planDisplayNames[planKey] || plan || 'Free'} Plan`}</strong>{' '}
        <Link target="_blank" href="/account">
          (view usage)
        </Link>
      </p>
      {isFreePlan && (
        <p className="m-0 p-0">
          {`Limited to ${planInfo.templates} templates & ${planInfo.quota} tests/day`}
        </p>
      )}
      {isFreePlan && (
        <ButtonLink
          id={upgradeButtonId}
          href={plansUrlWithUpgradeSource(source)}
          target="_blank"
          variant="upgrade"
          className="w-full gap-1.5 !font-normal"
          onClick={() =>
            trackUpgradeCtaClicked({
              source,
              plan: planKey,
            })
          }
        >
          <UpgradeIcon />
          Upgrade
        </ButtonLink>
      )}
    </div>
  );
}

export default NavPlanUpgrade;
